import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { UpgradeGate } from "@/components/UpgradeGate";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { CORE_ROUTES, PUBLIC_ROUTES, ROUTE_ALIASES } from "@/config/coreRoutes";
import { useRoutePersistence } from "@/hooks/useSessionLifecycle";
import { hasPendingPWAUpdate, applyPendingPWAUpdate } from "@/hooks/usePWAUpdate";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";

// Retry wrapper for lazy imports — handles stale chunks after rebuilds & network glitches.
// Strategy: 1) Wait 1s and re-import (handles transient network issues on mobile).
//           2) If that fails, do a full page reload (handles stale chunk hashes after deploy).
//           3) If reload already happened, give up and throw.
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch(async () => {
      // Retry 1: wait briefly and re-import (no reload)
      await new Promise((r) => setTimeout(r, 1000));
      try {
        return await importFn();
      } catch {
        // Retry 2: full page reload — but at most ONCE per 10s window, so a
        // persistently-missing chunk (e.g. stale service worker after a deploy)
        // recovers gracefully instead of looping forever.
        const last = Number(sessionStorage.getItem("chunk-reload-at") || "0");
        if (Date.now() - last > 10000) {
          sessionStorage.setItem("chunk-reload-at", String(Date.now()));
          window.location.reload();
          return new Promise(() => {}); // never resolves — page is reloading
        }
        // Recently reloaded and still failing — show a friendly fallback, do NOT loop.
        return {
          default: () => (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-8 text-center">
              <p className="text-lg font-semibold">Unable to load this page</p>
              <p className="text-muted-foreground text-sm max-w-md">
                This can happen on slow connections or after an app update. Please refresh the page or check your internet connection.
              </p>
              <button
                onClick={() => { sessionStorage.removeItem("chunk-reload-at"); window.location.reload(); }}
                className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90"
              >
                Refresh Page
              </button>
            </div>
          ),
        };
      }
    })
  );
}

// Note: the chunk-reload guard is time-based (see lazyWithRetry above) and
// self-expires after 10s, so it does NOT need to be cleared on every load —
// clearing it unconditionally here is what previously caused infinite reload loops.

const Auth = lazyWithRetry(() => import("@/pages/Auth"));
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"));
const BrandGuide = lazyWithRetry(() => import("@/pages/BrandGuide"));
const PreLive = lazyWithRetry(() => import("@/pages/PreLive"));
const PostLive = lazyWithRetry(() => import("@/pages/PostLive"));
const DuringLive = lazyWithRetry(() => import("@/pages/DuringLive"));
const Topics = lazyWithRetry(() => import("@/pages/Topics"));
const Settings = lazyWithRetry(() => import("@/pages/Settings"));
const Admin = lazyWithRetry(() => import("@/pages/Admin"));
const Credits = lazyWithRetry(() => import("@/pages/Credits"));
const CreditsPurchaseSuccess = lazyWithRetry(() => import("@/pages/CreditsPurchaseSuccess"));
const History = lazyWithRetry(() => import("@/pages/History"));
// VA-225: the Join page is retired — the route now redirects to the site.
const MembershipSuccess = lazyWithRetry(() => import("@/pages/MembershipSuccess"));
const AccountPaused = lazyWithRetry(() => import("@/pages/AccountPaused"));
const VisionAssistant = lazyWithRetry(() => import("@/pages/VisionAssistant"));
const FreePage = lazyWithRetry(() => import("@/pages/FreePage"));
const Services = lazyWithRetry(() => import("@/pages/Services"));
const Support = lazyWithRetry(() => import("@/pages/Support"));
const SupportReply = lazyWithRetry(() => import("@/pages/SupportReply"));
const ShareBrandGuide = lazyWithRetry(() => import("@/pages/ShareBrandGuide"));
const ConnectClaude = lazyWithRetry(() => import("@/pages/ConnectClaude"));
const MarketingAudit = lazyWithRetry(() => import("@/pages/MarketingAudit"));
const Agency = lazyWithRetry(() => import("@/pages/Agency"));
const CustomServices = lazyWithRetry(() => import("@/pages/CustomServices"));
const Coaching = lazyWithRetry(() => import("@/pages/Coaching"));
const ContentAssistant = lazyWithRetry(() => import("@/pages/ContentAssistant"));
const PrototypePreview = lazyWithRetry(() => import("@/pages/PrototypePreview"));
const SignupHandoff = lazyWithRetry(() => import("@/pages/SignupHandoff"));

// Redirects to an external URL outside the React Router tree
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <PageLoader />;
}

// Page loader component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function RootRoute() {
  const { user, loading, isInitialized } = useAuth();

  if (loading || !isInitialized) {
    return <PageLoader />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Auth />
    </Suspense>
  );
}

export function AnimatedRoutes() {
  const location = useLocation();

  // Persist current route so Chrome tab discard can restore it
  useRoutePersistence(location.pathname);

  // Auto-apply a pending PWA update on navigation. A route change is a natural,
  // low-risk reload point (the user is moving between pages, not mid-form), so a
  // newly deployed version reaches users automatically — no manual refresh, and
  // no reload loop (unlike the old tab-switch trigger we removed). Once applied,
  // the next page render runs the latest code straight from the server.
  // lazyWithRetry backstops any stale chunk if a reload is somehow missed.
  useEffect(() => {
    if (hasPendingPWAUpdate()) {
      applyPendingPWAUpdate();
    }
  }, [location.pathname]);

  return (
      <Routes location={location}>
        {/* Public routes */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/auth" element={<Navigate to="/" replace />} />
      <Route path="/auth/handoff" element={
        <Suspense fallback={<PageLoader />}>
          <SignupHandoff />
        </Suspense>
      } />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* VA-304: the OAuth approval screen Claude sends the member to. Not wrapped in
          ProtectedRoute — it redirects to sign-in itself, carrying its own query string, because
          losing those parameters means Claude's request is gone and the member starts over. */}
      <Route path="/connect-claude" element={<ConnectClaude />} />
      {/* VA-225: /join is retired. Bridget: "Do not rewrite the page. Retire it and
          send the traffic to the current site."
          The real redirect is a 301 in the Apache vhost, so bookmarks, old emails and
          search results update — a client-side redirect cannot do that. This is the
          fallback for anything that reaches the router anyway: a stale service worker,
          a cached bundle, or an environment without the vhost rule. */}
      <Route path="/join" element={<ExternalRedirect to="https://myvisionassistant.com" />} />
      <Route path="/join-free" element={<ExternalRedirect to="https://myvisionassistant.com" />} />
      <Route path="/community" element={<ExternalRedirect to="https://www.myvisionassistant.com" />} />
      <Route path="/free" element={
        <Suspense fallback={<PageLoader />}>
          <FreePage />
        </Suspense>
      } />
      <Route path="/share/:shareId" element={
        <Suspense fallback={<PageLoader />}>
          <ShareBrandGuide />
        </Suspense>
      } />
      <Route path="/membership-success" element={
        <Suspense fallback={<PageLoader />}>
          <MembershipSuccess />
        </Suspense>
      } />
      <Route path="/account-paused" element={
        <Suspense fallback={<PageLoader />}>
          <AccountPaused />
        </Suspense>
      } />
      
      {/* Protected routes with lazy loading */}
      <Route path="/change-password" element={
        <ProtectedRoute skipPasswordCheck>
          <ChangePassword />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <Dashboard />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/brand-guide" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <BrandGuide />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/brands" element={<Navigate to="/dashboard" replace />} />
      <Route path="/brand-voice" element={<Navigate to="/brand-guide" replace />} />
      <Route path="/customer-avatar" element={<Navigate to="/brand-guide" replace />} />

      {/* ── Marketing Audit: Free for all users ── */}
      <Route path="/marketing-audit" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <MarketingAudit />
          </Suspense>
        </ProtectedRoute>
      } />

      {/* ── Agency: the prototype's Agency area. Brand Reports is the built tab. ── */}
      <Route path={CORE_ROUTES.agency.path} element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            {/* Solopreneur-only (DB "reseller"). Kate, VA-215: both Free AND Starter
                could open this — the route had no gate at all. */}
            <UpgradeGate
              featureName="Agency"
              minTier="reseller"
              description="Client-facing delivery for the brands you manage — your white-label details, every brand's HighLevel connection in one place, and the reports you send your clients. Upgrade to Solopreneur to unlock this feature."
            >
              <Agency />
            </UpgradeGate>
          </Suspense>
        </ProtectedRoute>
      } />

      {/* ── Gated routes: Free users see UpgradeGate ── */}
      <Route path="/topics" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <UpgradeGate
              featureName="Topic Ideas"
              description="Generate AI-powered topic ideas based on your audience's pain points. Upgrade to Solopreneur to unlock this feature."
            >
              <Topics />
            </UpgradeGate>
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/generator" element={<Navigate to="/generator/pre-live" replace />} />
      <Route path="/pre-live" element={<Navigate to="/generator/pre-live" replace />} />
      <Route path="/post-live" element={<Navigate to="/generator/post-live" replace />} />
      <Route path="/generator/pre-live" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <UpgradeGate
              featureName="Pre-Live Content"
              description="Generate emails, social posts, and community content before your live session. Upgrade to Solopreneur to unlock this feature."
            >
              <PreLive />
            </UpgradeGate>
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/generator/during-live" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <UpgradeGate
              featureName="During Live"
              description="Watch Bridget's guide to running your live session for maximum content repurposing. Upgrade to Solopreneur to unlock this feature."
            >
              <DuringLive />
            </UpgradeGate>
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/generator/post-live" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <UpgradeGate
              featureName="Post-Live Content"
              description="Repurpose your live session into YouTube SEO, blog posts, and more. Upgrade to Solopreneur to unlock this feature."
            >
              <PostLive />
            </UpgradeGate>
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/vision-assistant" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <UpgradeGate
              featureName="Vision Assistant"
              description="Your AI marketing coach trained on your brand. Generate content, brainstorm ideas, and get strategic coaching. Upgrade to unlock."
            >
              <VisionAssistant />
            </UpgradeGate>
          </Suspense>
        </ProtectedRoute>
      } />
      {/* ── End gated routes ── */}

      {/* New-UI prototype, mounted as-is (full-screen, its own shell) */}
      <Route path="/prototype" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <PrototypePreview />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <Settings />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/plan" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <Credits />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/plan/purchase-success" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <CreditsPurchaseSuccess />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/credits" element={<Navigate to="/plan" replace />} />
      <Route path="/credits/*" element={<Navigate to="/plan" replace />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <Admin />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <History />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/services" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <Services />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/support/reply" element={
        <Suspense fallback={<PageLoader />}>
          <SupportReply />
        </Suspense>
      } />
      <Route path="/support" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <Support />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/custom-services" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <CustomServices />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/coaching" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <Coaching />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/content-assistant" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <UpgradeGate
              featureName="Content Assistant"
              description="Sync social accounts, manage publishing defaults, and schedule content to HighLevel Social Planner. Upgrade to Solopreneur to unlock this feature."
            >
              <ContentAssistant />
            </UpgradeGate>
          </Suspense>
        </ProtectedRoute>
      } />
        <Route path="/vision-chat" element={<Navigate to="/vision-assistant" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
}
